/**
 * `<AiRatingWidget>` — thumbs-up / thumbs-down rating widget for AI outputs.
 *
 * Design reference: backend/docs/batch_b12_design.md §1.
 * Batch B-12.
 *
 * Visual states:
 *   - unrated: ghost Lucide thumbs icons + tooltip.
 *   - up-selected: filled thumb-up; click again DELETEs.
 *   - down-pending-comment: filled thumb-down + inline textarea (1000-char).
 *       A DOWN vote REQUIRES a non-empty reason (founder decision) — Submit is
 *       disabled until the comment has content; there is no Skip path.
 *   - down-selected: collapsed comment display + Edit link; click again DELETEs.
 *
 * Race gate: `inFlight` state disables both buttons while a POST/DELETE is in
 * progress. Optimistic patch is applied before the request; reverted on error.
 *
 * null/prefix-mismatch modelUsed → renders nothing (no output to rate).
 *
 * Mobile <640px (P2-ai-rating-mobile-inline-expand): the comment-box footer
 * (char count + Submit) stacks vertically below 640px so the action button
 * gets full width instead of cramping under the thumbs; sm:+ keeps the
 * desktop inline (count left, button right) layout. The textarea is already
 * w-full at all widths.
 *
 * ts-prune: exported and consumed by AiEvaluationTab, InterviewPrepTab,
 * CandidatePageSidebar (B-12 surfaces) + InterviewAnalysisTab (B-13).
 */
import React, { useRef, useState, type JSX } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";

import { useRatingByResource, useSubmitRating, useClearRating } from "@/hooks/queries/ratings/useRatingByResource";
import { useToastStore } from "@/stores/useToastStore";
import type { ActionType } from "@/hooks/queries/ratings/useRatingByResource";

interface AiRatingWidgetProps {
  actionType: ActionType;
  resourceKey: string;
  modelUsed: string | null;
  className?: string;
}

const MAX_COMMENT = 1000;

export function AiRatingWidget({
  actionType,
  resourceKey,
  modelUsed,
  className,
}: AiRatingWidgetProps): JSX.Element | null {
  // Guard: null or prefix-mismatch → render nothing.
  if (!modelUsed) return null;
  const prefix = resourceKey.split(":", 1)[0];
  if (prefix !== actionType) return null;

  return (
    <AiRatingWidgetInner
      actionType={actionType}
      resourceKey={resourceKey}
      modelUsed={modelUsed}
      className={className}
    />
  );
}

// Inner component avoids conditional hook calls.
function AiRatingWidgetInner({
  actionType,
  resourceKey,
  modelUsed: _modelUsed,
  className,
}: AiRatingWidgetProps & { modelUsed: string }): JSX.Element {
  const addToast = useToastStore((s) => s.addToast);
  const ratingQ = useRatingByResource(resourceKey);
  const submitMutation = useSubmitRating();
  const clearMutation = useClearRating();

  const myRating = ratingQ.data?.my_rating ?? null;

  // inFlight: tracks whether a POST or DELETE is currently in-flight.
  const [inFlight, setInFlight] = useState(false);

  // Comment textarea state — only active in down-pending-comment phase.
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const currentRating = myRating?.rating ?? null;

  // Optimistic rollback data: store old data ref for revert.
  const optimisticPrev = useRef<typeof ratingQ.data>(undefined);

  async function doSubmit(
    rating: "up" | "down",
    commentText: string | null,
  ): Promise<void> {
    if (inFlight) return;
    setInFlight(true);
    optimisticPrev.current = ratingQ.data;
    try {
      await submitMutation.mutateAsync({
        actionType,
        resourceKey,
        rating,
        comment: commentText ?? undefined,
      });
      setShowCommentBox(false);
      setComment("");
    } catch {
      addToast("Couldn't save rating. Please try again.", "error");
    } finally {
      setInFlight(false);
    }
  }

  async function doClear(): Promise<void> {
    if (inFlight) return;
    setInFlight(true);
    try {
      await clearMutation.mutateAsync({ actionType, resourceKey });
      setShowCommentBox(false);
      setComment("");
    } catch {
      addToast("Couldn't remove rating. Please try again.", "error");
    } finally {
      setInFlight(false);
    }
  }

  function handleUpClick(): void {
    if (inFlight) return;
    if (currentRating === "up") {
      // Click already-selected → DELETE.
      void doClear();
    } else {
      // Clicking up when down-selected or unrated: submit up.
      setShowCommentBox(false);
      setComment("");
      void doSubmit("up", null);
    }
  }

  function handleDownClick(): void {
    if (inFlight) return;
    if (currentRating === "down") {
      // Click already-selected → DELETE.
      void doClear();
    } else {
      // Show comment box for down rating.
      setShowCommentBox(true);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }

  function handleCommentSubmit(): void {
    if (inFlight) return;
    // Founder decision: a DOWN vote REQUIRES a non-empty reason. Block submit
    // when the trimmed comment is empty (the Submit button is also disabled).
    const trimmed = comment.trim();
    if (!trimmed) return;
    void doSubmit("down", trimmed);
  }

  const isUpSelected = currentRating === "up";
  const isDownSelected = currentRating === "down";

  // A DOWN vote requires a reason (founder decision). Submit/Update is blocked
  // until the comment has non-whitespace content.
  const commentIsEmpty = comment.trim().length === 0;

  return (
    <div
      className={`flex flex-col gap-1 ${className ?? ""}`}
      data-testid="ai-rating-widget"
    >
      <div className="flex items-center gap-1.5">
        {/* Thumbs up */}
        <button
          type="button"
          disabled={inFlight}
          onClick={handleUpClick}
          aria-label={isUpSelected ? "Remove helpful rating" : "Mark as helpful"}
          aria-pressed={isUpSelected}
          title={isUpSelected ? "Rated helpful — click to undo" : "Mark as helpful"}
          data-testid="ai-rating-up"
          className={[
            "flex items-center rounded p-1 transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)]",
            "disabled:pointer-events-none disabled:opacity-50",
            isUpSelected
              ? "text-status-positive-fg"
              : "text-text-tertiary hover:text-text-secondary",
          ].join(" ")}
        >
          <ThumbsUp
            size={14}
            aria-hidden
            fill={isUpSelected ? "currentColor" : "none"}
            strokeWidth={1.75}
          />
        </button>

        {/* Thumbs down */}
        <button
          type="button"
          disabled={inFlight}
          onClick={handleDownClick}
          aria-label={isDownSelected ? "Remove unhelpful rating" : "Mark as unhelpful"}
          aria-pressed={isDownSelected}
          title={isDownSelected ? "Rated unhelpful — click to undo" : "Mark as unhelpful"}
          data-testid="ai-rating-down"
          className={[
            "flex items-center rounded p-1 transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)]",
            "disabled:pointer-events-none disabled:opacity-50",
            isDownSelected
              ? "text-status-rejected-fg"
              : "text-text-tertiary hover:text-text-secondary",
          ].join(" ")}
        >
          <ThumbsDown
            size={14}
            aria-hidden
            fill={isDownSelected ? "currentColor" : "none"}
            strokeWidth={1.75}
          />
        </button>

        {/* Inline count */}
        {ratingQ.data ? (
          <span
            className="select-none text-[10px] text-text-tertiary tabular-nums"
            data-testid="ai-rating-counts"
            aria-hidden
          >
            {ratingQ.data.counts.up > 0 ? `${ratingQ.data.counts.up} ↑` : null}
            {ratingQ.data.counts.up > 0 && ratingQ.data.counts.down > 0 ? " · " : null}
            {ratingQ.data.counts.down > 0 ? `${ratingQ.data.counts.down} ↓` : null}
          </span>
        ) : null}
      </div>

      {/* Down-selected: collapsed comment + Edit */}
      {isDownSelected && !showCommentBox && myRating?.comment ? (
        <div
          className="flex items-baseline gap-1.5"
          data-testid="ai-rating-down-collapsed"
        >
          <p className="max-w-[240px] truncate text-[10px] text-text-tertiary">
            "{myRating.comment}"
          </p>
          <button
            type="button"
            disabled={inFlight}
            onClick={() => {
              setComment(myRating.comment ?? "");
              setShowCommentBox(true);
              setTimeout(() => textareaRef.current?.focus(), 0);
            }}
            className="text-[10px] text-text-link underline underline-offset-2 hover:no-underline disabled:opacity-50"
            data-testid="ai-rating-edit-comment"
          >
            Edit
          </button>
        </div>
      ) : null}

      {/* Down-pending-comment: inline textarea */}
      {showCommentBox && !isDownSelected ? (
        <div
          className="flex flex-col gap-1.5"
          data-testid="ai-rating-comment-box"
        >
          <textarea
            ref={textareaRef}
            value={comment}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value.slice(0, MAX_COMMENT))}
            maxLength={MAX_COMMENT}
            placeholder="What could be improved? (required)"
            rows={3}
            disabled={inFlight}
            data-testid="ai-rating-comment-textarea"
            className={[
              "w-full resize-none rounded border border-border-default bg-surface-card",
              "p-2 text-[11px] text-text-primary placeholder:text-text-tertiary",
              "focus:border-border-focus focus:outline-none",
              "disabled:opacity-50",
            ].join(" ")}
          />
          {/* <640px: stack char-count above full-width action row so the
            * Submit button isn't cramped under the thumbs. sm:+ keeps the
            * desktop inline (count left, button right) layout.
            * A DOWN vote requires a reason — there is no Skip path; Submit is
            * disabled until the comment is non-empty. */}
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[10px] text-text-tertiary tabular-nums">
              {comment.length}/{MAX_COMMENT}
            </span>
            <div className="flex gap-1.5 max-sm:justify-end">
              <button
                type="button"
                disabled={inFlight || commentIsEmpty}
                onClick={handleCommentSubmit}
                data-testid="ai-rating-submit"
                className={[
                  "rounded bg-surface-card px-2 py-1 text-[11px] font-medium",
                  "border border-border-default text-text-primary",
                  "hover:bg-surface-elevated disabled:opacity-50 max-sm:flex-1",
                ].join(" ")}
              >
                {inFlight ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Edit comment re-opens the textarea while down-selected */}
      {showCommentBox && isDownSelected ? (
        <div
          className="flex flex-col gap-1.5"
          data-testid="ai-rating-comment-box"
        >
          <textarea
            ref={textareaRef}
            value={comment}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value.slice(0, MAX_COMMENT))}
            maxLength={MAX_COMMENT}
            placeholder="What could be improved? (required)"
            rows={3}
            disabled={inFlight}
            data-testid="ai-rating-comment-textarea"
            className={[
              "w-full resize-none rounded border border-border-default bg-surface-card",
              "p-2 text-[11px] text-text-primary placeholder:text-text-tertiary",
              "focus:border-border-focus focus:outline-none",
              "disabled:opacity-50",
            ].join(" ")}
          />
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[10px] text-text-tertiary tabular-nums">
              {comment.length}/{MAX_COMMENT}
            </span>
            <div className="flex gap-1.5 max-sm:justify-end">
              <button
                type="button"
                disabled={inFlight}
                onClick={() => {
                  setShowCommentBox(false);
                  setComment(myRating?.comment ?? "");
                }}
                data-testid="ai-rating-cancel-edit"
                className="rounded px-2 py-1 text-[11px] text-text-secondary hover:text-text-primary disabled:opacity-50 max-sm:flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={inFlight || commentIsEmpty}
                onClick={handleCommentSubmit}
                data-testid="ai-rating-submit"
                className={[
                  "rounded bg-surface-card px-2 py-1 text-[11px] font-medium",
                  "border border-border-default text-text-primary",
                  "hover:bg-surface-elevated disabled:opacity-50 max-sm:flex-1",
                ].join(" ")}
              >
                {inFlight ? "Submitting…" : "Update"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
